import {Component, OnInit} from '@angular/core';
import {FileUploadService} from 'src/app/services/file-upload.service';
import {TranslateService} from '@ngx-translate/core';
import {map} from 'rxjs/operators';
import {HttpEventType} from '@angular/common/http';
import {MatDialog} from '@angular/material/dialog';
import {ActivatedRoute, Router} from '@angular/router';
import {ImportDetailsDialogComponent} from './import-details-dialog/import-details-dialog.component';
import {Err} from '../../err';
import {AddAccountDialogComponent} from '../account/add/add-account-dialog.component';
import {NotificationService} from '../../services/notification.service';
import {InformDTO} from '../shared/notification/InformDTO';
import {FileUploadResponseDTO} from '../../model/fileUploadResponseDTO';
import {ImportDetailsDialogResultModel} from './import-details-dialog/import-details-dialog-result.model';

@Component({
  templateUrl: './import.component.html',
  styleUrls: ['./import.component.scss']
})
export class ImportComponent implements OnInit {

  constructor(
    private service: FileUploadService,
    public dialog: MatDialog,
    private route: ActivatedRoute,
    private notificationService: NotificationService,
    private router: Router,
    public translate: TranslateService) {
    translate.use('nl');
  }

  status = {PENDING: 'PENDING', DELAYED: 'DELAYED', DONE: 'DONE', FAILED: 'FAILED'};
  report: Array<FileUploadResponseDTO>;

  clientId;

  public files = [];
  isUploading = false;
  bank: string;

  ngOnInit() {
    this.route.params.subscribe(params => {
       this.bank = params['bank'];
       this.clientId = params['clientId'];
    });
  }

  async upload() {
    this.isUploading = true;
    const data = this.getFileListData();
    try {
      for (const file of this.files.filter(f => f.status === this.status.PENDING)) {
        await this.uploadFile(file);
      } // throws exception
      for (const file of this.files.filter(f => f.status === this.status.DELAYED)) {
        await this.uploadFile(file);
      } // throws exception
      if (data.failed === 0) {
        this.files = [];
      }
    } catch (error) {
      if (error === 'RESTART') {
        await this.upload();
      } else {
        console.log(error);
      }
    }
    this.isUploading = false;
  }

  getFileListData() {
    let pending = 0;
    let delayed = 0;
    let done = 0;
    let failed = 0;
    for (const file of this.files) {
      switch (file.status) {
        case undefined:
        case this.status.PENDING:
          file.status = this.status.PENDING;
          pending++;
          break;
        case this.status.DELAYED:
          delayed++;
          break;
        case this.status.DONE:
          done++;
          break;
        case this.status.FAILED:
          failed++;
          break;
      }
    }
    return {pending: pending, delayed: delayed, done: done, failed: failed, total: pending + delayed + done + failed};
  }

  uploadFile(file) {
    return new Promise((resolve, reject) => {
      const formData = new FormData();
      formData.append('file', file);
      file.inProgress = true;
      this.service.upload(formData, this.clientId).pipe(
        map(event => {
          switch (event.type) {
            case HttpEventType.UploadProgress:
              file.progress = Math.round(event.loaded * 100 / event.total);
              break;
            case HttpEventType.Response:
              return event;
          }
        })).subscribe((event: any) => {
        if (typeof (event) === 'object') {
          file.status = this.status.DONE;
          this.updateReport(event.body).then(result => {
            switch (result) {
              case ImportDetailsDialogResultModel.ImportAnotherFile:
                this.router.navigate([`/bewind/${this.clientId}/importeren`]);
                break;
              case ImportDetailsDialogResultModel.CategorizeTransactions:
                this.router.navigate([`/bewind/${this.clientId}`]);
                break;
            }
            resolve();
          });
        }
      }, error => {
        const errorCode = error.error.message;
        if (errorCode.split(' ')[0] === 'ACCOUNT_NOT_PRESENT') {
          let iban = errorCode.split(' ')[1];
          if (iban.indexOf('EUR') !== -1) {
            iban = iban.substr(0, iban.indexOf('EUR'));
          }
          const data = {data: {iban: iban, clientId: this.clientId}, disableClose: true};
          this.dialog.open(AddAccountDialogComponent, data).componentInstance.promise().catch(() => {
            file.status = this.status.FAILED;
            file.error = 'Mislukt: Rekening is niet toegevoegd.';
          }).finally(() => reject('RESTART'));
        } else {
          if (errorCode === 'IMPORT_RABOBANK_OFFICE_CORRUPTION')  {
            this.showRabobankOfficeCorruptionError();
          }
          file.status = this.status.FAILED;
          file.error = Err[errorCode] !== undefined ? Err[errorCode] : errorCode;
          reject();
        }
      });
    });
  }

  /**
   * on file drop handler
   */
  onFileDropped($event: FileList) {
    console.log($event.constructor.name);
    this.prepareFilesList($event);
  }

  /**
   * handle file from browsing
   */
  fileBrowseHandler($event: Event) {
    const fileList = ($event.target as HTMLInputElement).files;
    this.prepareFilesList(fileList);
  }

  /**
   * Delete file from files list
   * @param index (File index)
   */
  deleteFile(index: number) {
    this.files.splice(index, 1);
  }

  /**
   * Convert Files list to normal array list
   * @param files (Files List)
   */
  prepareFilesList(files: FileList) {
    for (const file of Array.from(files)) {
      const fileWithProgress = file as File & { progress: number };
      fileWithProgress.progress = 0;
      this.files.push(fileWithProgress);
    }
  }

  /**
   * format bytes
   * @param bytes (File size in bytes)
   * @param decimals (Decimals point)
   */
  formatBytes(bytes, decimals) {
    if (bytes === 0) {
      return '0 Bytes';
    }
    const k = 1024;
    const dm = decimals <= 0 ? 0 : decimals || 2;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];

  }

  canUpload() {
    return !this.isUploading && this.files.some(file => !file.inProgress && file.progress === 0);
  }

  reset(file) {
    file.error = null;
    file.status = this.status.PENDING;
    file.progress = 0;
    file.inProgress = false;
  }

  private updateReport(rows: Array<FileUploadResponseDTO>) {
    const allDoneAndWell = this.files.filter(f => f.status !== this.status.DONE).length === 0;
    return this.dialog.open(ImportDetailsDialogComponent, {data: {rows: rows, allDoneAndWell: allDoneAndWell}}).componentInstance.promise();
  }

  private showRabobankOfficeCorruptionError() {
    const data: InformDTO = {
      header: 'bestand is beschadigd',
      ok: 'opnieuw downloaden',
      body: 'Dit bestand is na het downloaden geopend in Excel waardoor het bestand is beschadigd.<br>' +
            'Download opnieuw het bankbestand, <strong>open het bestand niet in Excel</strong>. <br>' +
            'Voor meer informatie bekijk de <a class="link" href="https://www.reveon.nl/help/inlezen-banktransacties/" target="_blank">helppagina</a> ' +
            'of gebruik onze chat rechtsonder om een gesprek te starten met een van onze medewerkers.'
    };
    this.notificationService.inform(data);
  }
}
